import { readStoredLanguage } from '../i18n/storedLanguage';
import { translate } from '../i18n/translate';

import type { ApiError } from './ApiError';
import { ApiNetworkError, ApiRequestError } from './errors';
import type { SessionKind } from './SessionKind';
import { clearSession, readSession, writeSession } from './storage';

export const API_BASE_URLS: Record<SessionKind, string> = {
  business: (import.meta.env.VITE_BUSINESS_API_URL as string | undefined) ?? 'http://localhost:3100/api/v1',
  platform: (import.meta.env.VITE_ADMIN_API_URL as string | undefined) ?? 'http://localhost:3101/api/v1',
};

type TokenPairResponse = { tokens: { accessToken: string; refreshToken: string } };

let refreshInFlight: Promise<boolean> | null = null;

const parseError = async (response: Response): Promise<ApiRequestError> => {
  try {
    return new ApiRequestError((await response.json()) as ApiError);
  } catch {
    // `statusText` is the server's English reason phrase, not copy for a person.
    return new ApiRequestError({
      statusCode: response.status,
      message: translate(readStoredLanguage(), 'error.requestFailed'),
      errorCode: 'UNKNOWN',
      correlationId: '',
    });
  }
};

const attemptRefresh = async (kind: SessionKind): Promise<boolean> => {
  const session = readSession();

  if (!session || session.kind !== kind) {
    return false;
  }

  refreshInFlight ??= (async (): Promise<boolean> => {
    try {
      let response: Response;

      try {
        response = await fetch(`${API_BASE_URLS[kind]}/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        });
      } catch {
        // A refresh that never left the machine says nothing about the token; keep it.
        return false;
      }

      if (!response.ok) {
        clearSession();

        return false;
      }

      const result = (await response.json()) as TokenPairResponse;

      writeSession({
        kind,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
      });

      return true;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

/**
 * One request to one API, with the session's token, one silent refresh on a 401, and an `ApiRequestError`
 * for anything not ok. Callers read the body the way they need it.
 */
const send = async (
  kind: SessionKind,
  path: string,
  init: RequestInit,
  retryOnUnauthorized: boolean,
): Promise<Response> => {
  const session = readSession();
  const headers = new Headers(init.headers);

  headers.set('content-type', 'application/json');
  headers.set('accept-language', readStoredLanguage());

  // A token is only ever sent to the API that minted it.
  if (session && session.kind === kind) {
    headers.set('authorization', `Bearer ${session.accessToken}`);
  }

  let response: Response;

  try {
    response = await fetch(`${API_BASE_URLS[kind]}${path}`, { ...init, headers });
  } catch (cause) {
    // eslint-disable-next-line no-console
    console.warn(`[api] Network failure on ${path}:`, cause);

    throw new ApiNetworkError(API_BASE_URLS[kind], cause);
  }

  if (response.status === 401 && retryOnUnauthorized && (await attemptRefresh(kind))) {
    return send(kind, path, init, false);
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  return response;
};

export const request = async <TResult>(
  kind: SessionKind,
  path: string,
  init: RequestInit = {},
): Promise<TResult> => {
  const response = await send(kind, path, init, true);

  // 204, and a 202 that accepts without saying anything (`/auth/password/forgot`), have no body to parse.
  const body = await response.text();

  if (body === '') {
    return undefined as TResult;
  }

  return JSON.parse(body) as TResult;
};

/** A file the API answers with — an invoice PDF — as a blob, with the same session and errors as JSON. */
export const requestBlob = async (kind: SessionKind, path: string): Promise<Blob> =>
  (await send(kind, path, {}, true)).blob();

/** The body of an error response an XHR received — the same parsing as `parseError`, without a `Response`. */
const parseXhrError = (xhr: XMLHttpRequest): ApiRequestError => {
  try {
    return new ApiRequestError(JSON.parse(xhr.responseText) as ApiError);
  } catch {
    return new ApiRequestError({
      statusCode: xhr.status,
      message: translate(readStoredLanguage(), 'error.requestFailed'),
      errorCode: 'UNKNOWN',
      correlationId: '',
    });
  }
};

/**
 * An upload that reports its progress — `fetch` cannot — with the session's token, one silent refresh on a 401 and
 * the same two error classes as every other request. `onProgress` gets 0…1 of the bytes sent. A form is sent as
 * multipart; a file on its own is the body itself (R0041), streamed by the API into storage.
 */
export const upload = <TResult>(
  kind: SessionKind,
  path: string,
  body: FormData | Blob,
  onProgress: (sent: number) => void,
  method: 'POST' | 'PUT' = 'POST',
  retryOnUnauthorized: boolean = true,
): Promise<TResult> =>
  new Promise<TResult>((resolve, reject) => {
    const session = readSession();
    const xhr = new XMLHttpRequest();

    xhr.open(method, `${API_BASE_URLS[kind]}${path}`);
    xhr.setRequestHeader('accept-language', readStoredLanguage());

    if (!(body instanceof FormData)) {
      xhr.setRequestHeader('content-type', 'application/octet-stream');
    }

    if (session && session.kind === kind) {
      xhr.setRequestHeader('authorization', `Bearer ${session.accessToken}`);
    }

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onerror = (): void =>
      reject(new ApiNetworkError(API_BASE_URLS[kind], new Error(`Upload to ${path} failed`)));
    xhr.onload = (): void => {
      if (xhr.status === 401 && retryOnUnauthorized) {
        void attemptRefresh(kind).then((refreshed: boolean) =>
          refreshed
            ? upload<TResult>(kind, path, body, onProgress, method, false).then(resolve, reject)
            : reject(parseXhrError(xhr)),
        );

        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(parseXhrError(xhr));

        return;
      }

      resolve((xhr.responseText === '' ? undefined : JSON.parse(xhr.responseText)) as TResult);
    };
    xhr.send(body);
  });

/**
 * The address of a one-time download link an API answered (R0041): the browser opens it itself — no token, saved
 * natively with its own progress — and it is served by the API, never by the storage behind it.
 */
export const downloadLinkUrl = (kind: SessionKind, path: string): string => `${API_BASE_URLS[kind]}${path}`;

export type ApiVerbs = {
  get: <TResult>(path: string) => Promise<TResult>;
  post: <TResult>(path: string, body: unknown) => Promise<TResult>;
  patch: <TResult>(path: string, body: unknown) => Promise<TResult>;
  put: <TResult>(path: string, body: unknown) => Promise<TResult>;
  delete: <TResult>(path: string) => Promise<TResult>;
  blob: (path: string) => Promise<Blob>;
  upload: <TResult>(
    path: string,
    body: FormData | Blob,
    onProgress: (sent: number) => void,
    method?: 'POST' | 'PUT',
  ) => Promise<TResult>;
};

/** The verbs, bound to one API. `api/admin/endpoints.ts` and `api/business/endpoints.ts` each hold one. */
export const createApi = (kind: SessionKind): ApiVerbs => ({
  get: <TResult>(path: string) => request<TResult>(kind, path),
  post: <TResult>(path: string, body: unknown) =>
    request<TResult>(kind, path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <TResult>(path: string, body: unknown) =>
    request<TResult>(kind, path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <TResult>(path: string, body: unknown) =>
    request<TResult>(kind, path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <TResult>(path: string) => request<TResult>(kind, path, { method: 'DELETE' }),
  blob: (path: string) => requestBlob(kind, path),
  upload: <TResult>(
    path: string,
    body: FormData | Blob,
    onProgress: (sent: number) => void,
    method: 'POST' | 'PUT' = 'POST',
  ) => upload<TResult>(kind, path, body, onProgress, method),
});
